import { useRef } from "react";
import { RotateCcw, X } from "lucide-react";
import type {
  CheckinAuditEntry,
  EnrichedCheckinItem,
} from "@/features/checkin/types";
import {
  canUndoReceivingHistoryEvent,
  canUndoReturnHistoryEvent,
  checkHistoryUndoReason,
  receivingHistorySource,
  receivingHistoryUndoReason,
  filterItemAuditEvents,
  formatCheckinAuditOperation,
  getCheckinAuditOperation,
} from "@/features/checkin/pageDisplay";
import { useDialogFocus } from "@/hooks/useDialogFocus";

function eventDetail(event: CheckinAuditEntry) {
  const quantity = event.payload.quantity;
  const note =
    event.payload.note ??
    event.payload.request_note ??
    event.payload.decision_note;
  return [
    typeof quantity === "number" ? `Quantidade: ${quantity}` : null,
    typeof note === "string" && note ? note : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function CheckinHistoryDrawer({
  open,
  item,
  events,
  canUndoReceiving,
  readOnly,
  closed,
  isLoading,
  error,
  pending,
  onUndoReceiving,
  onUndoReturn,
  onUndoCheck,
  onClose,
}: {
  open: boolean;
  item: EnrichedCheckinItem;
  events: CheckinAuditEntry[];
  canUndoReceiving: boolean;
  readOnly: boolean;
  closed: boolean;
  isLoading: boolean;
  error: boolean;
  pending: boolean;
  onUndoReceiving?: (event: CheckinAuditEntry) => void;
  onUndoReturn?: (event: CheckinAuditEntry) => void;
  onUndoCheck?: (event: CheckinAuditEntry) => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useDialogFocus(dialogRef, onClose, {
    enabled: open,
    initialFocusRef: closeRef,
  });
  if (!open) return null;

  const itemEvents = filterItemAuditEvents(events, item.id);
  return (
    <div className="checkin-history-layer" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="checkin-history-drawer detail-modal history-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`checkin-history-title-${item.id}`}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="detail-modal-head">
          <div>
            <span>HISTÓRICO DO ITEM</span>
            <h2 id={`checkin-history-title-${item.id}`}>{item.product_name}</h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Fechar histórico"
          >
            <X size={18} />
          </button>
        </header>
        <div className="detail-modal-body">
          <div className="checkin-history-events history-timeline">
            {isLoading ? <p>Carregando histórico…</p> : null}
            {error ? (
              <p role="alert">Não foi possível carregar o histórico.</p>
            ) : null}
            {!isLoading && !error && itemEvents.length === 0 ? (
              <p>Nenhum evento registrado.</p>
            ) : null}
            {(error || isLoading ? [] : itemEvents).map((event) => {
              const operation = getCheckinAuditOperation(event);
              const checkReason =
                operation === "CHECK_ITEM" || operation === "QUICK_CHECK_ITEM"
                  ? checkHistoryUndoReason(
                      event,
                      itemEvents,
                      item,
                      canUndoReceiving && !readOnly,
                      closed,
                    )
                  : null;
              return (
                <article key={event.id} className="history-event">
                  <div>
                    <strong>
                      {formatCheckinAuditOperation(
                        getCheckinAuditOperation(event),
                      )}
                    </strong>
                    <span>
                      {event.changed_by_name ?? "Usuário"} ·{" "}
                      {new Intl.DateTimeFormat("pt-BR", {
                        dateStyle: "short",
                        timeStyle: "short",
                        timeZone: "America/Sao_Paulo",
                      }).format(new Date(event.changed_at))}
                    </span>
                    {eventDetail(event) ? <p>{eventDetail(event)}</p> : null}
                    {onUndoCheck &&
                    (operation === "CHECK_ITEM" ||
                      operation === "QUICK_CHECK_ITEM") ? (
                      <>
                        <button
                          type="button"
                          className="history-event-undo"
                          disabled={pending || Boolean(checkReason)}
                          onClick={() => onUndoCheck(event)}
                          aria-label={`Desfazer conferência ${operation === "QUICK_CHECK_ITEM" ? "rápida" : "registrada"} de ${new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" }).format(new Date(event.changed_at))}`}
                        >
                          <RotateCcw size={16} /> Desfazer conferência
                        </button>
                        {checkReason ? (
                          <p className="history-event-reason">{checkReason}</p>
                        ) : null}
                      </>
                    ) : null}
                    {onUndoReceiving &&
                    canUndoReceiving &&
                    receivingHistorySource(event, item) &&
                    receivingHistoryUndoReason(
                      event,
                      item,
                      canUndoReceiving,
                      closed,
                      itemEvents,
                    ) ? (
                      <p className="history-event-reason">
                        {receivingHistoryUndoReason(
                          event,
                          item,
                          canUndoReceiving,
                          closed,
                          itemEvents,
                        )}
                      </p>
                    ) : null}
                    {onUndoReturn &&
                    !readOnly &&
                    canUndoReturnHistoryEvent(
                      event,
                      events,
                      item,
                      canUndoReceiving,
                      closed,
                    ) ? (
                      <button
                        type="button"
                        className="history-event-undo"
                        disabled={pending}
                        onClick={() => onUndoReturn(event)}
                      >
                        <RotateCcw size={16} />{" "}
                        {getCheckinAuditOperation(event) === "DECIDE_RETURN"
                          ? "Corrigir decisão"
                          : "Desfazer devolução"}
                      </button>
                    ) : null}
                    {onUndoReceiving &&
                    !readOnly &&
                    !closed &&
                    canUndoReceivingHistoryEvent(
                      event,
                      itemEvents,
                      item,
                      canUndoReceiving,
                    ) ? (
                      <button
                        type="button"
                        className="history-event-undo"
                        disabled={pending}
                        onClick={() => onUndoReceiving(event)}
                        aria-label={`Desfazer ${formatCheckinAuditOperation(getCheckinAuditOperation(event))} de ${new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" }).format(new Date(event.changed_at))}`}
                      >
                        <RotateCcw size={16} /> Desfazer
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
